/**
 * Job detail: the customer satisfaction survey of this job (tracker item 69).
 * Shows whether a survey was asked for, sent, answered or never sent (and
 * why), the rating and comment once answered (low scores highlighted), why
 * the survey mode would not send this job by itself, and a Send / Resend
 * button. ADMIN and OFFICE_MANAGER only (the page renders it for office roles).
 * The customer's comment is untrusted text: it is rendered as plain text.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ISurveysApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { newIdempotencyKey, NOTIFICATION_SETTINGS_PATH } from '../pages/settings/notificationUi';
import { REQUEST_STATUS_CLASSES, REQUEST_STATUS_LABELS, requestSummaryText, SURVEY_SETTINGS_PATH, ratingLabel } from '../pages/surveys/surveyUi';
import { secondaryButtonClass } from './formStyles';
import { StarDisplay } from './StarRating';
import { Toast } from './Toast';

interface Props {
  api: Pick<ISurveysApi, 'getJobSurvey' | 'sendJobSurvey'>;
  jobId: string;
  /** Reloads when the job's status changes (e.g. completed) */
  jobStatus: string;
}

const tap = ' min-h-[44px] sm:min-h-0';

export function JobSurveySection({ api, jobId, jobStatus }: Props) {
  const [state, setState] = useState<types.JobSurvey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await api.getJobSurvey(jobId));
      setError(null);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the customer survey'));
    }
  }, [api, jobId]);

  useEffect(() => { void load(); }, [load, jobStatus]);

  async function send() {
    if (busy || !state?.canSend) return;
    const resend = !!state.request && state.request.sendCount > 0;
    const question = resend
      ? 'Email the customer a new survey link? The link they were sent before will stop working.'
      : 'Email the customer the satisfaction survey now?';
    if (!window.confirm(question)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.sendJobSurvey(jobId, newIdempotencyKey());
      setNotice(
        result.deduplicated
          ? 'This was already queued a moment ago.'
          : result.message.status === 'SKIPPED'
            ? `Not sent: ${result.message.skipReasonText ?? result.message.skipReason}`
            : 'The survey email is queued.'
      );
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not send the survey'));
    } finally {
      setBusy(false);
    }
  }

  const request = state?.request ?? null;
  const response = state?.response ?? null;
  return (
    <section aria-labelledby="job-survey-heading" className="bg-white rounded-lg shadow p-5 sm:p-7" data-testid="job-survey">
      <h2 id="job-survey-heading" className="text-lg font-semibold mb-3">Customer survey</h2>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {state === null ? (
        !error && <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 text-sm">
              {request ? (
                <>
                  <p className="font-medium text-gray-900">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-semibold ${REQUEST_STATUS_CLASSES[request.status]}`}
                      data-testid="job-survey-status"
                    >
                      {REQUEST_STATUS_LABELS[request.status]}
                    </span>
                  </p>
                  <p className="mt-1 text-gray-700">{requestSummaryText(request)}</p>
                </>
              ) : (
                <p className="text-gray-700">No survey has been requested for this job.</p>
              )}
              {!request && state.automatic.reason && (
                <p className="mt-1 text-xs text-gray-500">Not scheduled automatically: {state.automatic.reason}.</p>
              )}
              {!request && state.automatic.eligible && (
                <p className="mt-1 text-xs text-gray-500">It will be scheduled automatically when the job is completed.</p>
              )}
              {state.canSend && state.wouldSkipText && (
                <p className="mt-1 text-xs text-amber-800">It would not be sent: {state.wouldSkipText}.</p>
              )}
              {!state.canSend && state.unavailableReason && (
                <p className="mt-1 text-xs text-gray-500">{state.unavailableReason}</p>
              )}
              {!state.enabled && (
                <p role="note" className="mt-2 rounded bg-gray-50 p-2 text-xs text-gray-700">
                  The survey message is off.{' '}
                  <Link to={SURVEY_SETTINGS_PATH} className="text-blue-700 underline">Settings &gt; Customer Surveys</Link>{' · '}
                  <Link to={NOTIFICATION_SETTINGS_PATH} className="text-blue-700 underline">Settings &gt; Notifications</Link>
                </p>
              )}
            </div>
            <button type="button" className={`${secondaryButtonClass}${tap}`} disabled={busy || !state.canSend} title={state.unavailableReason ?? undefined} onClick={() => void send()}>
              {request && request.sendCount > 0 ? 'Resend survey' : 'Send survey'}
            </button>
          </div>

          {response && (
            <div
              className={`mt-4 rounded-lg border p-3 ${response.lowScore ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
              data-testid="job-survey-response"
            >
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                <StarDisplay rating={response.rating} className="text-lg" />
                <span>{response.rating} of 5 - {ratingLabel(response.rating)}</span>
                {response.lowScore && <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800">Low score: follow up</span>}
              </p>
              <p className="mt-1 text-xs text-gray-500">{new Date(response.createdAt).toLocaleString()}</p>
              {response.comment ? (
                <blockquote className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{response.comment}</blockquote>
              ) : (
                <p className="mt-2 text-sm text-gray-500">No comment.</p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
